import type { RichTextEditorHandle } from '@shared/components/atoms/RichTextEditor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import { useCommentQueryActions } from '@/api/hooks/commentQueryActions';
import type {
  ApiCommentType,
  CreateCommentRequest,
  EstimationCommentResponse,
} from '@/api/schema/data-contracts';
import { ApiCommentContextType, ApiEstimationStage } from '@/api/schema/data-contracts';
import IconSend from '@/assets/Icons/IconSend';
import FieldErrors from '@/components/atoms/FieldErrors';
import { Loader } from '@/components/atoms/Loader';
import { SanitizedHtml } from '@/components/atoms/SanitizedHtml';
import { WysiwygTextField } from '@/components/atoms/WysiwygTextField';
import { UserFullName } from '@/components/UserFullName';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useAuth } from '@/context/auth';
import { stripHtml } from '@/helpers/estimation';
import { useMessages } from '@/hooks/messages';
import { useEstimationStage } from '@/hooks/useEstimationStage';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import { sortExpressions } from '@/utils/sorting';

import { commentsMessageDefinitions } from './messages';

type CommentsProps = {
  estimationId: string;
  estimationItemId: string;
  commentType: ApiCommentType;
  commentContext: ApiCommentContextType;
};

const Comments: React.FC<CommentsProps> = ({
  estimationId,
  estimationItemId,
  commentType,
  commentContext,
}) => {
  const { user } = useAuth();
  const messages = useMessages(commentsMessageDefinitions);
  const lastCommentAnchorRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<RichTextEditorHandle>(null);

  const { useCreateNewComment, useGetComments } = useCommentQueryActions();
  const { data, isFetched, isLoading } = useGetComments(estimationItemId, commentType) as {
    data: EstimationCommentResponse[] | undefined;
    isFetched: boolean;
    isLoading: boolean;
  };
  const { mutateAsync: sendNewComment, isPending: isCreateNewCommentPending } =
    useCreateNewComment();

  const estimationStage = useEstimationStage(estimationId);

  const hasCanCommentPermission = useHasEstimationPermission(estimationId, 'CAN_COMMENT');

  const canComment =
    hasCanCommentPermission === true && estimationStage === ApiEstimationStage.IN_REVIEW;

  const [messageContent, setMessageContent] = useState('');

  const scrollToBottom = () => {
    if (lastCommentAnchorRef.current) {
      lastCommentAnchorRef.current.scrollIntoView();
    }
  };

  useEffect(() => {
    if (isFetched) scrollToBottom();
  }, [isFetched]);

  // EWM-1012: the editor only exists once loading and the permission check
  // both resolve, which happens after the host modal's one-shot focus trap
  // already ran and found nothing. Focus it explicitly instead of relying on
  // that trap's initial scan. Both gates are dependencies: the comments query
  // and the permission/stage queries are independent, so whichever resolves
  // last is the render that finally mounts the editor - keying on `canComment`
  // alone misses the order where it flips true while `isLoading` still short-
  // circuits the render to <Loader />, leaving `editorRef` null.
  useEffect(() => {
    if (canComment && !isLoading) editorRef.current?.focus();
  }, [canComment, isLoading]);

  const sortedComments = useMemo(
    () => data?.toSorted((a, b) => sortExpressions.byCreatedAt(a, b)),
    [data],
  );

  const commentTooLong = useMemo(
    () =>
      messageContent.length > entityFieldSettings.commentEntity.textMaxLength ||
      stripHtml(messageContent).length >
        entityFieldSettings.commentEntity.textStrippedHtmlMaxLength,
    [messageContent],
  );

  const getContextTypeName = useCallback(
    (contextType: ApiCommentContextType) => {
      switch (contextType) {
        case ApiCommentContextType.ESTIMATION:
          return messages.contextTypeEstimationLabel();
        case ApiCommentContextType.RESOURCING:
          return messages.contextTypeResourcingLabel();
        case ApiCommentContextType.COSTING:
          return messages.contextTypeCostingLabel();
      }
    },
    [messages],
  );

  const handleSendMessageClick = async () => {
    if (messageContent.trim()) {
      const commentRequestData: CreateCommentRequest = {
        text: messageContent,
        commentType: commentType,
        contextType: commentContext,
        entityId: estimationItemId,
      };

      await sendNewComment(commentRequestData, { onSuccess: () => setMessageContent('') });
    }
  };

  const handleMessageKeyUp = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && event.ctrlKey && !isCreateNewCommentPending) {
      await handleSendMessageClick();
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  const hasComments = sortedComments && sortedComments.length > 0;

  return (
    <div className='flex max-h-[72vh] flex-col'>
      <div className='flex-1 overflow-y-auto p-4'>
        {!hasComments && (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <div className='dark:bg-c4 mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='text-gray-400'
              >
                <path
                  d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <p className='text-sm text-gray-400'>{messages.emptyState()}</p>
          </div>
        )}
        {sortedComments?.map((message) => {
          const isOwnMessage = user?.id === message.userId;

          return (
            <div key={message.id}>
              <div className={`flex items-start gap-3 ${isOwnMessage ? 'justify-end' : ''}`}>
                <div className={`flex-none ${isOwnMessage ? 'order-2' : ''}`}>
                  <span
                    className='bg-c5 dark:bg-c4 flex h-9 w-9 items-center justify-center rounded-full object-cover'
                    title='userProfile'
                  >
                    <UserFullName userId={message.userId} returnInitials />
                  </span>
                </div>
                <div className='space-y-1.5 pb-6'>
                  <div className='flex items-center gap-3'>
                    <div
                      className={`min-w-full rounded-lg px-3 py-2 text-sm shadow-xs ${
                        isOwnMessage
                          ? 'dark:bg-c4 ltr:rounded-br-none rtl:rounded-bl-none dark:text-white'
                          : 'dark:bg-c4 bg-gray-100 text-gray-900 ltr:rounded-bl-none rtl:rounded-br-none dark:text-gray-100'
                      }`}
                    >
                      <small className='text-xxs'>{getContextTypeName(message.contextType)}</small>
                      <br />
                      <SanitizedHtml htmlString={message.text} />
                    </div>
                  </div>
                  <div
                    className={`text-[11px] text-gray-400 ${isOwnMessage ? 'ltr:text-right rtl:text-left' : ''}`}
                  >
                    <UserFullName userId={message.userId} /> /{' '}
                    <FormattedDate
                      value={message.createdAt}
                      day='numeric'
                      month='long'
                      year='numeric'
                    />
                    , <FormattedTime value={message.createdAt} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={lastCommentAnchorRef} />
      </div>
      {canComment && (
        <div className='sticky bottom-0 p-4'>
          <div className='relative'>
            <label className='block w-full'>
              {messages.messageLabel()}
              <WysiwygTextField
                ref={editorRef}
                className='w-full'
                placeholder={messages.typeMessagePlaceholder()}
                readOnly={isCreateNewCommentPending}
                autoFocus
                value={messageContent}
                onChange={(value) => setMessageContent(value)}
                onKeyUp={handleMessageKeyUp}
              />
              {commentTooLong && <FieldErrors error={{ type: 'maxLength' }} />}
            </label>
            <button
              disabled={isCreateNewCommentPending || commentTooLong || messageContent.length === 0}
              type='button'
              className={`absolute right-2 bottom-2 pb-5 hover:${isCreateNewCommentPending ? '' : 'text-primary'} ltr:right-4 rtl:left-4`}
              onClick={() => handleSendMessageClick()}
            >
              {isCreateNewCommentPending && (
                <div className='flex'>
                  <span className='text-s mr-2'>{messages.postingComment()}</span>
                  <Loader size='s' noMargin />
                </div>
              )}
              {!isCreateNewCommentPending && <IconSend />}
            </button>
            <small className='mt-1 block text-right text-xs'>{messages.hintTitle()}</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comments;
